import { ComplexValue } from 'app/types-shared';
import { Filter, CustomFilterType, FilterType } from 'app/area/search-filters/filters/types';
import { filterId, fieldType } from 'app/area/search-filters/constants';
import { queryFilters } from '../constants';
import { endpoint } from '../endpoints';
import { getFieldId } from 'app/area/search-filters/filters/selectors';
import { RoleFiltersRenderConditions } from 'app/area/administration/list/roles/types';

export const roleQueryFilters = (translation): ComplexValue<string>[] => [
  { value: queryFilters.name, description: translation.name },
  { value: queryFilters.id, description: translation.role_id }
];

// eslint:disable-next-line:max-func-body-length
export const roleFilters = ({ readOnlyDefaultFilterPickers, hasRestrictedAccess }: RoleFiltersRenderConditions, translation): Filter[] => [
  {
    id: filterId.roleCode,
    label: translation.code,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.inputIntegerFilter,
        includeInSummary: true
      }
    ],
    type: FilterType.TEXT_RANGE
  },
  {
    id: filterId.createdDate,
    label: translation.created_date,
    fields: [
      {
        fieldTypeId: fieldType.date,
        type: CustomFilterType.dateRange,
        includeInSummary: true
      }
    ],
    type: FilterType.DATE_RANGE,
    options: {
      dateToAsEndOfDay: true
    }
  },
  {
    id: filterId.accessToOperation,
    label: translation.access_to_operation,
    fields: [
      {
        fieldTypeId: fieldType.application,
        label: translation.application_or_case_type,
        type: CustomFilterType.codeHierarchyParents,
        optionsEndpoint: endpoint.roleApplication,
        divider: true
      },
      {
        fieldTypeId: fieldType.root,
        label: translation.operation,
        type: CustomFilterType.code,
        optionsEndpoint: endpoint.roleAccessToOperation,
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.accessToScreen,
    label: translation.access_to_screen,
    fields: [
      {
        fieldTypeId: fieldType.application,
        label: translation.case_type,
        type: CustomFilterType.codeHierarchyParents,
        optionsEndpoint: endpoint.caseType,
        optionsParams: { includeSynergiLifeApplication: false },
        divider: true
      },
      {
        fieldTypeId: fieldType.root,
        label: translation.screen,
        type: CustomFilterType.code,
        optionsEndpoint: endpoint.accessToScreen,
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.extendedAccess,
    label: translation.extended_access,
    fields: [
      {
        fieldTypeId: fieldType.application,
        label: translation.case_type,
        type: CustomFilterType.codeHierarchyParents,
        optionsEndpoint: endpoint.caseType,
        optionsParams: { includeSynergiLifeApplication: false },
        divider: true
      },
      {
        fieldTypeId: fieldType.root,
        label: translation.unit,
        type: CustomFilterType.codeHierarchy,
        optionsEndpoint: endpoint.responsibleForUnit,
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  hasRestrictedAccess && {
    id: filterId.restrictedAccess,
    label: translation.restricted_access,
    fields: [
      {
        fieldTypeId: fieldType.application,
        label: translation.case_type,
        type: CustomFilterType.codeHierarchyParents,
        optionsEndpoint: endpoint.caseType,
        optionsParams: { includeSynergiLifeApplication: false },
        divider: true
      },
      {
        fieldTypeId: fieldType.root,
        label: translation.unit,
        type: CustomFilterType.codeHierarchy,
        optionsEndpoint: endpoint.responsibleForUnit,
        optionsParams: { displayNotSelected: true }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.fields,
    label: translation.visible_for_fields,
    fields: [
      {
        fieldTypeId: fieldType.root,
        type: CustomFilterType.codeWithoutExclude,
        optionsEndpoint: endpoint.visibleForFields,
        optionsParams: { displayNotSelected: true, levels: undefined }
      }
    ],
    type: FilterType.SELECTED_IDS
  },
  {
    id: filterId.defaultValue,
    label: translation.default_values,
    fields: [
      {
        fieldTypeId: fieldType.codetable,
        label: translation.option_list,
        type: CustomFilterType.defaultValueSingleHierarchy,
        optionsEndpoint: endpoint.defaultValues.optionList,
        includeInSummary: true,
        defaultValueParams: {
          cleanValues: [
            getFieldId(filterId.defaultValue, fieldType.defaultValues),
            getFieldId(filterId.defaultValue, fieldType.domains)
          ]
        }
      },
      {
        fieldTypeId: fieldType.defaultValues,
        label: translation.default_value,
        type: CustomFilterType.defaultValueMultipleSubLevel,
        optionsEndpoint: endpoint.defaultValues.defaultValues,
        defaultValueParams: {
          readOnly: readOnlyDefaultFilterPickers,
          codetableId: getFieldId(filterId.defaultValue, fieldType.codetable)
        }
      },
      {
        fieldTypeId: fieldType.domains,
        label: translation.field,
        type: CustomFilterType.defaultValueMultiple,
        optionsEndpoint: endpoint.defaultValues.domains,
        optionsParams: { includeOptionsForAllUserTypes: true },
        defaultValueParams: {
          readOnly: readOnlyDefaultFilterPickers,
          codetableId: getFieldId(filterId.defaultValue, fieldType.codetable)
        }
      },
      {
        fieldTypeId: fieldType.application,
        label: translation.case_type,
        type: CustomFilterType.defaultValueSingleHierarchyParents,
        optionsEndpoint: endpoint.defaultValues.applications,
        defaultValueParams: {
          codetableId: getFieldId(filterId.defaultValue, fieldType.codetable)
        }
      }
    ],
    type: FilterType.DEFAULT_VALUE
  }
].filter(filter => !!filter);
